import createSelector from 'web-app/util/custom-create-selector';
import {feedItemIsObservation} from 'web-app/react/entities/feed/items/helpers';
import {observationToFormData} from 'web-app/react/entities/observations/helpers';
import ObservationBehaviors from 'web-app/behaviors/observation-behavior-ids';
import {hasValue} from 'web-app/util/typescript';
import * as NewPostHelpers from 'web-app/react/feed/post/new-post/helpers';
import {getValueForRichTextBody} from 'web-app/react/entities/feed/items/helpers';

const feedItem = state => state.modals.draftManager.feedItem;
const newPostRecipients = state => state.modals.draftManager.newPostRecipients;
const selectedRecipientsFromState = state => state.modals.draftManager.selectedRecipients;

export const isObservation = createSelector(feedItem, feedItem => feedItem && feedItemIsObservation(feedItem));

export const postId = createSelector(feedItem, isObservation, (feedItem, isObservation) => {
    if (!feedItem) {
        return undefined;
    }

    if (isObservation) {
        return feedItem.embed.get('observationId');
    }

    return feedItem.originatorId.replace('Post:', ''); // Harrington told me to do this 😎
});

export const draftManagerIsEditting = createSelector(postId, postId => hasValue(postId));

export const selectedRecipients = createSelector(
    newPostRecipients,
    feedItem,
    selectedRecipientsFromState,
    (newPostRecipients, feedItem, selectedRecipientsFromState) => {
        if (selectedRecipientsFromState) {
            return selectedRecipientsFromState;
        }

        if (!feedItem) {
            return newPostRecipients;
        }

        const observation = feedItem.embed.get('observation');
        if (!observation) {
            return undefined;
        }

        return newPostRecipients || observation.children.map(({id}) => id).join(',');
    },
);

/**
 * Initial values are used to populate new-post/observation editor with an existing feedItem
 * If no feedItem is set the modal with the editor is opened anyway as the assumed action is
 * to write a new post / a new observation. In such case the initialValues selector returns
 * value of undefined.
 * However, there's an additional use case where we want to pre-fill the editor with a custom
 * content. For such use case it's possible to use "initialValues" property available on this reducer.
 * Note that for content to be returned from this selector feedItem must be unset as it takes
 * precedence over custom post content passed via "initialValues" property.
 */
export const initialValues = createSelector(
    feedItem,
    state => state.modals.draftManager.initialValues,
    isObservation,
    (state, props) => props.canUseRichTextInPosts,
    (feedItem, initialValues, isObservation, canUseRichTextInPosts) => {
        // If feedItem is unset check if initialValues are set
        if (!feedItem) {
            // If initialValues are set return it as assummed action is to
            // pre-populate editor fields
            if (initialValues) {
                return initialValues;
            }

            return undefined;
        }

        if (isObservation) {
            const observation = feedItem.embed.get('observation');
            if (!observation) {
                return undefined;
            }

            return observationToFormData(observation, true);
        }

        const richTextBody = getValueForRichTextBody(feedItem);
        return {
            body: canUseRichTextInPosts ? undefined : feedItem.body,
            richTextBody: canUseRichTextInPosts ? richTextBody : undefined,
            disableFeedback: feedItem.disableFeedback,
            activities: NewPostHelpers.embedToActivityFormValues(feedItem.embed),
            /**
             * This is supposed to be initial values for when editing a post.
             * In this case the selectedRecipients are simply an array of names in plain text.
             * In the UI we render them plainly as a string in a TextField component, hence this transformation.
             */
            selectedRecipients: feedItem.receivers.join(','),
        };
    },
);

export const observationVariant = createSelector(
    state => state.modals.draftManager.observationVariant,
    isObservation,
    feedItem,
    (observationVariant, isObservation, feedItem) => {
        if (isObservation && feedItem) {
            const observation = feedItem.embed.get('observation');
            return observation.variant;
        }

        return observationVariant;
    },
);

export const behaviors = createSelector(isObservation, feedItem, (isObservation, feedItem) => {
    if (isObservation && feedItem) {
        const observation = feedItem.embed.get('observation');
        return observation.behaviors;
    }

    return null;
});

export const canSendObservation = createSelector(behaviors, behaviors => {
    return behaviors && behaviors.includes(ObservationBehaviors.CanEditObservation);
});
